import React, { useContext, useState } from 'react';
import { Editor, NodeViewContent, NodeViewWrapper } from '@tiptap/react';
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
  Tooltip,
  makeStyles,
} from '@fluentui/react-components';
import { MoreHorizontal20Regular, DeleteRegular, TextWrapRegular } from '@fluentui/react-icons';

import { liveSectionPiece } from '@modules/documents/largeDocument';

import DocumentContext from '../../../contexts/DocumentContext.context';
import { SECTION_CLASS_NAME } from './constants';

const useStyles = makeStyles({
  actionsButton: {
    minWidth: '28px',
  },
});

type TProps = {
  editor: Editor;
  getPos: () => number | undefined; // Position of this section node in the document
};

/**
 * React component for rendering a landscape section node.
 * Displays a three-dots dropdown menu in the top-right corner (on hover, in edit mode).
 * Menu options: Unwrap section, Delete section.
 */
const SectionNodeComponent = ({ editor, getPos }: TProps) => {
  const { isEdit } = useContext(DocumentContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Anything but `only` means this node is one slice of a section the editor does not wholly hold, so
  // it must not draw the page's edges, and unwrapping or deleting it would leave the rest behind.
  const piece = liveSectionPiece();
  const classes = useStyles();

  // Use getPos() to target this specific section (works even if cursor is outside)
  // Use chain().focus() to ensure editor regains focus after menu interaction
  const handleUnwrap = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(false);
    const pos = getPos();
    if (pos === undefined) return;
    editor.chain().focus().unwrapSection(pos).run();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen(false);
    const pos = getPos();
    if (pos === undefined) return;
    editor.chain().focus().deleteSection(pos).run();
  };

  return (
    <NodeViewWrapper as="section" className={SECTION_CLASS_NAME} data-section-piece={piece ?? undefined}>
      {isEdit && piece !== 'first' && piece !== 'middle' && piece !== 'last' && (
        <div className="section-actions" contentEditable={false}>
          <Menu open={isMenuOpen} onOpenChange={(_, data) => setIsMenuOpen(data.open)}>
            <MenuTrigger disableButtonEnhancement>
              <Tooltip content="Page options" relationship="label" withArrow>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<MoreHorizontal20Regular />}
                  className={classes.actionsButton}
                  onClick={(e) => e.stopPropagation()}
                />
              </Tooltip>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<TextWrapRegular />} onClick={handleUnwrap}>
                  Convert to portrait
                </MenuItem>
                <MenuItem icon={<DeleteRegular />} onClick={handleDelete}>
                  Delete page
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      )}

      {/* Section content */}
      <NodeViewContent />
    </NodeViewWrapper>
  );
};

export default SectionNodeComponent;
